import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Collapse,
  Paper,
  Chip,
  Stack,
  Pagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import { tokens } from "../../../theme";
import superAdminService from "../../../services/SüperAdminServices/superAdminService";
import DataTable from "../../../components/SüperAdminComponents/DataTable";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export default function FormAnswersPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [formAnswers, setFormAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    patientName: "",
    formName: "",
    doctorName: "",
    type: "",
    pointMin: "",
    pointMax: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await superAdminService.getAllFormAnswers({
        params: appliedFilters,
        headers: {
          "x-bone-pageindex": pageIndex,
          "x-bone-limit": pageSize,
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      setFormAnswers(response.data || []);
      setTotalCount(Number(response.headers?.["x-total-count"] || 0));
      setTotalPages(Number(response.headers?.["x-total-pages"] || 1));
    } catch (error) {
      setSnackbar({ open: true, message: "Error fetching form answers", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pageIndex, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleView = (item) => {
    setSelectedItem(item);
    setViewDialogOpen(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await superAdminService.deleteFormAnswer(selectedItem.id);
      setSnackbar({ open: true, message: "Form answer deleted successfully", severity: "success" });
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: "Error deleting form answer", severity: "error" });
    }
  };

  const sanitizeFilters = useMemo(() => () => {
    const output = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        output[key] = value;
      }
    });
    return output;
  }, [filters]);

  const getFilenameFromHeaders = (headers) => {
    const contentDisposition = headers?.["content-disposition"] || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || `form-answers-export-${Date.now()}.xlsx`;
  };

  const applyFilters = () => {
    setPageIndex(0);
    setAppliedFilters(sanitizeFilters());
  };

  const resetFilters = () => {
    const emptyFilters = {
      patientName: "",
      formName: "",
      doctorName: "",
      type: "",
      pointMin: "",
      pointMax: "",
      dateFrom: "",
      dateTo: "",
    };
    setFilters(emptyFilters);
    setPageIndex(0);
    setAppliedFilters({});
  };

  const activeFilterCount = Object.keys(appliedFilters || {}).length;

  const handleExcelExport = async () => {
    try {
      setExportLoading(true);
      const response = await superAdminService.exportExcel({
        resource: "formAnswers",
        filters: appliedFilters,
        sortBy,
        sortOrder,
      });

      const fileName = getFilenameFromHeaders(response.headers);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: "Excel downloaded successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Excel export failed", severity: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    { id: "name", label: "Answer Name" },
    { id: "Patient", label: "Patient", format: (value) => value ? `${value.firstName || ""} ${value.lastName || ""}`.trim() || "-" : "-" },
    { id: "Form", label: "Form", format: (value) => value?.name || "-" },
    { id: "Doctor", label: "Doctor", format: (value) => value ? `${value.name || ""} ${value.surname || ""}`.trim() || "-" : "-" },
    { id: "point", label: "Point" },
    { id: "type", label: "Type" },
  ];

  return (
    <Box sx={{ 
      p: 3, 
      width: "100%", 
      minWidth: "100%", 
      maxWidth: "100%", 
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, width: "100%", minWidth: "100%" }}>
        <AssignmentIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
        <Typography variant="h2" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          Form Answers Management
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              endIcon={filtersOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              onClick={() => setFiltersOpen((prev) => !prev)}
              sx={{
                color: colors.grey[100],
                backgroundColor: filtersOpen ? colors.blueAccent[700] : colors.primary[300],
                "&:hover": {
                  backgroundColor: filtersOpen ? colors.blueAccent[800] : colors.primary[200],
                },
              }}
            >
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExcelExport}
              disabled={exportLoading}
              sx={{
                backgroundColor: colors.blueAccent[600],
                "&:hover": { backgroundColor: colors.blueAccent[700] },
              }}
            >
              {exportLoading ? "Exporting..." : "Export Excel"}
            </Button>
          </Stack>
        </Box>
      </Box>

      <Collapse in={filtersOpen} timeout={250} unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="h6" sx={{ color: colors.grey[100], fontWeight: 600 }}>
              Advanced Filters
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {activeFilterCount > 0 && (
                <Chip
                  size="small"
                  label={`${activeFilterCount} active`}
                  sx={{
                    backgroundColor: `${colors.greenAccent[600]}40`,
                    color: colors.greenAccent[200],
                    border: `1px solid ${colors.greenAccent[600]}70`,
                  }}
                />
              )}
              <Tooltip title="Clear all filters">
                <span>
                  <IconButton
                    onClick={resetFilters}
                    size="small"
                    sx={{ color: colors.grey[200] }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5, mb: 1.5 }}>
            <TextField
              label="Patient Name"
              size="small"
              value={filters.patientName}
              onChange={(event) => setFilters((prev) => ({ ...prev, patientName: event.target.value }))}
            />
            <TextField
              label="Form Name"
              size="small"
              value={filters.formName}
              onChange={(event) => setFilters((prev) => ({ ...prev, formName: event.target.value }))}
            />
            <TextField
              label="Doctor Name"
              size="small"
              value={filters.doctorName}
              onChange={(event) => setFilters((prev) => ({ ...prev, doctorName: event.target.value }))}
            />
            <TextField
              select
              label="Type"
              size="small"
              value={filters.type}
              onChange={(event) => setFilters((prev) => ({ ...prev, type: event.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="for me">for me</MenuItem>
              <MenuItem value="for patients">for patients</MenuItem>
            </TextField>
            <TextField
              label="Point Min"
              type="number"
              size="small"
              value={filters.pointMin}
              onChange={(event) => setFilters((prev) => ({ ...prev, pointMin: event.target.value }))}
            />
            <TextField
              label="Point Max"
              type="number"
              size="small"
              value={filters.pointMax}
              onChange={(event) => setFilters((prev) => ({ ...prev, pointMax: event.target.value }))}
            />
            <TextField
              label="Date From"
              type="date"
              size="small"
              value={filters.dateFrom}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date To"
              type="date"
              size="small"
              value={filters.dateTo}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={applyFilters}>
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              onClick={resetFilters}
              startIcon={<RestartAltIcon />}
              sx={{ borderColor: `${colors.grey[100]}35`, color: colors.grey[100] }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>
      </Collapse>
      <DataTable columns={columns} data={formAnswers} onView={handleView} onDelete={handleDelete} loading={loading} emptyMessage="No form answers found" />
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          border: `1px solid ${colors.grey[800]}30`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={`Total ${totalCount}`}
              sx={{ backgroundColor: `${colors.blueAccent[700]}50`, color: colors.grey[100] }}
            />
            <Chip
              size="small"
              label={`Page ${pageIndex + 1}/${totalPages}`}
              sx={{ backgroundColor: `${colors.primary[300]}60`, color: colors.grey[100] }}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            select
            label="Rows per page"
            size="small"
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value));
              setPageIndex(0);
            }}
            sx={{ minWidth: 100 }}
          >
            {[10, 20, 50, 100].map((size) => (
              <MenuItem key={size} value={size}>{size}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Sort By"
            size="small"
            value={sortBy}
            onChange={(event) => {
              setSortBy(event.target.value);
              setPageIndex(0);
            }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="createdAt">Created</MenuItem>
            <MenuItem value="answered_at">Answered At</MenuItem>
            <MenuItem value="point">Point</MenuItem>
          </TextField>
          <TextField
            select
            label="Direction"
            size="small"
            value={sortOrder}
            onChange={(event) => {
              setSortOrder(event.target.value);
              setPageIndex(0);
            }}
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="DESC">DESC</MenuItem>
            <MenuItem value="ASC">ASC</MenuItem>
          </TextField>
          </Stack>
          <Pagination
            color="primary"
            count={Math.max(totalPages, 1)}
            page={Math.min(pageIndex + 1, Math.max(totalPages, 1))}
            onChange={(_, page) => setPageIndex(page - 1)}
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      </Paper>
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` } }}>
        <DialogTitle sx={{ color: colors.grey[100] }}>Form Answer Details</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>Patient</Typography>
              <Typography variant="body1" sx={{ color: colors.grey[100], mb: 2 }}>
                {selectedItem.Patient ? `${selectedItem.Patient.firstName || ""} ${selectedItem.Patient.lastName || ""}`.trim() || "-" : "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>Form</Typography>
              <Typography variant="body1" sx={{ color: colors.grey[100], mb: 2 }}>{selectedItem.Form?.name || "-"}</Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>Doctor</Typography>
              <Typography variant="body1" sx={{ color: colors.grey[100] }}>{selectedItem.Doctor ? `${selectedItem.Doctor.name} ${selectedItem.Doctor.surname}` : "-"}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} sx={{ color: colors.grey[100] }}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` } }}>
        <DialogTitle sx={{ color: colors.grey[100] }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.grey[200] }}>Are you sure you want to delete this form answer? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: colors.grey[100] }}>Cancel</Button>
          <Button onClick={confirmDelete} sx={{ color: colors.redAccent[400] }}>Delete</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

